import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DispatchContext } from '../../../store';
import { SNACKBAR_OPEN } from '../../../reducers';
import Moment from 'react-moment';
import { catalogueApi } from '../../../services/CatalogueServices';
import { useNavigate } from 'react-router';
import KeywordSearch from '../../../common/SearchComponent/KeywordSearch';

export default function CatalogueLists({ status, activeTask, meta, setMeta, role }) {
  const dispatch = useContext(DispatchContext);
  const navigate = useNavigate();
  const [querySearch, setQuerySearch] = useState('');
  const [artefacts, setArtefacts] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedState, setSelectedState] = useState(null);

  const allStateOptions = [
    { value: 'initiated', label: 'Initiated' },
    { value: 'requested', label: 'Requested' },
    { value: 'resubmitted', label: 'Resubmitted' },
    { value: 'forwarded_to_curator', label: 'Forwarded to Curator' },
    { value: 'forwarded_to_admin', label: 'Forwarded to Admin' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'rejected_for_changes', label: 'Rejected for Changes' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'closed', label: 'Closed' }
  ];

  // Filter state options based on user role
  const stateOptions = allStateOptions.filter(option => {
    // Only allow forwarded_to_curator for roles 3, 5, and 6
    if (option.value === 'forwarded_to_curator') {
      return role?.id === 3 || role?.id === 5 || role?.id === 6;
    }
    return true;
  });

  const asText = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      return value?.name || value?.code || value?.description || String(value?.id || '-');
    }
    const str = String(value);
    return str?.trim?.() ? str : '-';
  };

  useEffect(() => {
    getUserList();
  }, [querySearch, page, rowsPerPage, selectedState]);

  const getUserList = () => {
    const params = {
      search: querySearch,
      page: page,
      per_page: rowsPerPage
    };
    
    // New Task tab: always filter by active_task instead of a specific state
    if (activeTask) {
      params.active_task = true;
    } else if (status) {
      // Add state filter from prop if provided
      params.state = status;
    }

    // Add state filter if selected from dropdown (overrides prop/activeTask)
    if (selectedState && selectedState.value) {
      delete params.active_task;
      params.state = selectedState.value;
    }
    
    catalogueApi('get', null, params).then(res => {
      setArtefacts(res?.data);
      setMeta(res?.meta);
    })
      .catch(err => console.log(err))
  };

  const handleChangePage = (event, newPage) => {
    dispatch({ type: 'IS_NOT_LOADING', payload: false });
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const artefactDetail = (id) => {
    // Find the artefact to check its status
    const artefact = artefacts.find(a => a.id === id);
    
    // Only allow editing if status is 'rejected_for_changes'
    if (artefact?.state === 'rejected_for_changes') {
      navigate(`/management/catalogue-artefacts/${id}`)
    } else if (artefact?.state === 'rejected') {
      // Show message that rejected items cannot be edited
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { 
          isNotify: true, 
          severity: 'error', 
          message: 'Rejected catalogues cannot be edited. Only catalogues rejected for changes can be edited.' 
        }
      });
    } else {
      navigate(`/management/catalogue-artefacts/${id}`)
    }
  }

  const handleSearchChange = (value) => {
    setQuerySearch(value);
  }

  const handleStateFilterChange = (event) => {
    const selectedValue = event.target.value;
    const selectedOption = selectedValue ? stateOptions.find(option => option.value === selectedValue) : null;
    setSelectedState(selectedOption);
    setPage(1); 
  }

  return (
    <div>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item sm={4} xs={12}>
          <KeywordSearch placeholder={'Search...'} onChange={(value) => handleSearchChange(value)} />
        </Grid>
        <Grid item sm={4} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="state-filter-label">Filter by State</InputLabel>
            <Select
              labelId="state-filter-label"
              id="state-filter"
              value={selectedState?.value || ''}
              onChange={handleStateFilterChange}
              label="Filter by State"
            >
              <MenuItem value="">
                <em>All States</em>
              </MenuItem>
              {stateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <TableContainer className={'box-shadow'} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="caption table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ color: '#B3B3B3', width: 20 }}>Sl.No</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>Date of Entry</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>Accession No.</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>Object Name</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>Materials</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>Place of Origin</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>Cataloged By</TableCell>
              <TableCell align="left" sx={{ color: '#B3B3B3' }}>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {artefacts?.length > 0 && artefacts?.map((artefact, index) => (
              <TableRow hover key={index}>
                <TableCell align="left" sx={{ color: '#3D3D3D', width: 20 }} onClick={() => artefactDetail(artefact?.id)}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  <Moment format={'ll'}>{artefact?.date_of_entry || '-'}</Moment>
                </TableCell>
                <TableCell align="left" sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  {asText(artefact?.accession_no || artefact?.accession_no_legacy)}
                </TableCell>
                <TableCell align="left" sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  {artefact?.object_name || '-'}
                </TableCell>
                <TableCell align="left" sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  {asText(artefact?.material)}
                </TableCell>
                <TableCell align="left" sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  {asText(artefact?.place_of_origin)}
                </TableCell>
                <TableCell align="left" sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  {artefact?.user_name || '-'}
                </TableCell>
                <TableCell align="left" sx={{ color: '#3D3D3D' }} onClick={() => artefactDetail(artefact?.id)}>
                  {artefact?.state ? artefact?.state.charAt(0).toUpperCase() + artefact?.state.slice(1).replace('_', ' ') : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 100]}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        component="div"
        count={meta?.total}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}